import { useLocalStorage } from 'react-use';
import { useSyncExternalStore } from 'use-sync-external-store/shim';

export default function useLocalStorageSync<T>(
  key: string,
  initialValue?: T
): [T | undefined, (value: T) => void] {
  // 값이 없을 때 initialValue를 localStorage에 저장하는 등의 동작을 위해 useLocalStorage를 사용합니다.
  const [, setValue] = useLocalStorage<T>(key, initialValue);

  const value = useSyncExternalStore(
    (onStoreChange) => {
      const callback = (event: StorageEvent) => {
        if (event.key === key) {
          onStoreChange();
        }
      };

      window.addEventListener('storage', callback);
      return () => {
        window.removeEventListener('storage', callback);
      };
    },
    () => window.localStorage.getItem(key)
  );

  return [
    parseLocalStorageValue<T>(value, initialValue),
    (value: T) => {
      setValue(value);

      // storage 이벤트는 액션이 일어난 window에서는 발생하지 않기 때문에 이벤트를 수동으로 발생시켜줍니다.
      // https://developer.mozilla.org/en-US/docs/Web/API/Window/storage_event
      window.dispatchEvent(new StorageEvent('storage', { key }));
    },
  ];
}

function parseLocalStorageValue<T>(value: string | null, initialValue?: T) {
  // 값이 없거나 JSON 형태가 아니면 initialValue를 반환합니다.
  try {
    if (!value) {
      return initialValue;
    }

    // useLocalStorage의 기본 deserializer와 동일한 로직을 사용합니다.
    return JSON.parse(value) as T;
  } catch (error) {
    return initialValue;
  }
}
